import { Howl } from 'howler';

/**
 * AudioEngine - Wrapper around Howler.js for podcast playback
 * Provides a clean API for audio playback, seeking, speed control, and events
 */
export class AudioEngine {
  constructor() {
    this.howl = null;
    this.currentUrl = null;

    // Event callbacks
    this.onplay = null;
    this.onpause = null;
    this.onend = null;
    this.onseek = null;
    this.onload = null;
    this.onerror = null;
    this.ontimeupdate = null;

    // Time update interval
    this.timeUpdateInterval = null;
  }

  /**
   * Load a new audio file
   * @param {string} url - Audio file URL
   * @param {boolean} autoplay - Auto-play after loading
   * @param {Object} metadata - Episode metadata for MediaSession
   */
  load(url, autoplay = false, metadata = {}) {
    // Clean up existing howl
    if (this.howl) {
      this.howl.unload();
      this.stopTimeUpdate();
    }

    this.currentUrl = url;

    // Set MediaSession metadata for lock screen / notification controls
    this.setMediaSessionMetadata(metadata);

    this.howl = new Howl({
      src: [url],
      html5: true,        // Enable streaming for large files
      preload: true,
      autoplay: autoplay,
      onload: () => {
        if (this.onload) this.onload();
      },
      onplay: () => {
        this.startTimeUpdate();
        if (this.onplay) this.onplay();
      },
      onpause: () => {
        this.stopTimeUpdate();
        if (this.onpause) this.onpause();
      },
      onend: () => {
        this.stopTimeUpdate();
        if (this.onend) this.onend();
      },
      onseek: () => {
        if (this.onseek) this.onseek();
      },
      onloaderror: (id, error) => {
        console.error('Audio load error:', error);
        if (this.onerror) this.onerror(error);
      },
      onplayerror: (id, error) => {
        console.error('Audio play error:', error);
        if (this.onerror) this.onerror(error);
      }
    });
  }

  /**
   * Set MediaSession metadata for lock screen / notification controls
   * @param {Object} metadata - Episode metadata
   */
  setMediaSessionMetadata(metadata = {}) {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: metadata.title || 'Unknown Episode',
        artist: metadata.artist || 'Hippocratic Society',
        album: metadata.album || 'Hippocratic Society Podcast',
        artwork: metadata.artwork || []
      });
    }
  }

  /**
   * Start playback
   */
  play() {
    if (this.howl && !this.howl.playing()) {
      this.howl.play();
    }
  }

  /**
   * Pause playback
   */
  pause() {
    if (this.howl && this.howl.playing()) {
      this.howl.pause();
    }
  }

  /**
   * Toggle play/pause
   */
  togglePlay() {
    if (this.howl) {
      if (this.howl.playing()) {
        this.pause();
      } else {
        this.play();
      }
    }
  }

  /**
   * Seek to a specific time
   * @param {number} time - Time in seconds
   */
  seek(time) {
    if (this.howl) {
      this.howl.seek(time);
    }
  }

  /**
   * Get current playback time
   * @returns {number} Current time in seconds
   */
  getCurrentTime() {
    return this.howl ? this.howl.seek() : 0;
  }

  /**
   * Get total duration
   * @returns {number} Duration in seconds
   */
  getDuration() {
    return this.howl ? this.howl.duration() : 0;
  }

  /**
   * Check if audio is currently playing
   * @returns {boolean}
   */
  isPlaying() {
    return this.howl ? this.howl.playing() : false;
  }

  /**
   * Set playback speed
   * @param {number} rate - Playback rate (1.0 = normal, 2.0 = 2x speed)
   */
  setRate(rate) {
    if (this.howl) {
      this.howl.rate(rate);
    }
  }

  /**
   * Get current playback speed
   * @returns {number} Current playback rate
   */
  getRate() {
    return this.howl ? this.howl.rate() : 1.0;
  }

  /**
   * Set volume
   * @param {number} volume - Volume level (0.0 to 1.0)
   */
  setVolume(volume) {
    if (this.howl) {
      this.howl.volume(volume);
    }
  }

  /**
   * Get current volume
   * @returns {number} Volume level (0.0 to 1.0)
   */
  getVolume() {
    return this.howl ? this.howl.volume() : 1.0;
  }

  /**
   * Start time update interval
   * Fires ontimeupdate callback every 50ms during playback for smooth progress
   */
  startTimeUpdate() {
    this.stopTimeUpdate(); // Clear any existing interval

    this.timeUpdateInterval = setInterval(() => {
      if (this.ontimeupdate && this.howl && this.howl.playing()) {
        const currentTime = this.getCurrentTime();
        const duration = this.getDuration();
        this.ontimeupdate(currentTime, duration);
      }
    }, 50);
  }

  /**
   * Stop time update interval
   */
  stopTimeUpdate() {
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
      this.timeUpdateInterval = null;
    }
  }

  /**
   * Unload and cleanup
   */
  unload() {
    this.stopTimeUpdate();
    if (this.howl) {
      this.howl.unload();
      this.howl = null;
    }
    this.currentUrl = null;
  }
}
